import React, { useEffect, useState } from 'react';

import { Checkbox, Form, Input, InputNumber, Select } from 'antd';

const { TextArea } = Input;
const { Option } = Select;
const identity = (formData) => formData;

export const INITIAL_PRICE_FORM_DATA = {
  description: '',
  type: 'MOT',
  engineType: '',
  category: '',
  status: 'active',
};

const normalizeNumber = (value) => {
  if (value === '' || value == null) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const normalizePriceFormData = (price = {}) => ({
  ...price,
  description: price.description ?? '',
  type: price.type ?? 'MOT',
  engineType: price.engineType ?? '',
  category: price.category ?? '',
  accountCode: normalizeNumber(price.accountCode),
  accountCodePurchases: normalizeNumber(price.accountCodePurchases),
  itemCode: price.itemCode ?? '',
  vatExempt: Boolean(price.vatExempt),
  status: price.status ?? 'active',
});

const buildPricePayload = (formData) => {
  /** @type {Record<string, unknown>} */
  const payload = Object.entries(formData).reduce((acc, [key, value]) => {
    if (value === '' || value == null) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

  if (payload.marketPrice == null) {
    payload.marketPrice = 0;
  }

  if (payload.fixedCost == null) {
    payload.fixedCost = 0;
  }

  return payload;
};

export const buildCreatePricePayload = buildPricePayload;

export const buildUpdatePricePayload = buildPricePayload;

export const usePriceFormState = (initialFormData, normalizeFormData = identity) => {
  const [formData, setFormData] = useState(() => normalizeFormData(initialFormData));

  useEffect(() => {
    setFormData(normalizeFormData(initialFormData));
  }, [initialFormData, normalizeFormData]);

  const updateField = (field, value) => {
    setFormData((currentData) => ({
      ...currentData,
      [field]: value,
    }));
  };

  return { formData, updateField };
};

export function PriceFormFields({
  formData,
  onFieldChange,
  showStatus = false,
  showAccountingFields = false,
}) {
  return (
    <>
      <Form.Item label="Description" required>
        <TextArea value={formData.description} onChange={(event) => onFieldChange('description', event.target.value)} />
      </Form.Item>

      <Form.Item
        label="Accounting Type"
        extra="This field is used for accounting and reporting. Please select the correct type."
      >
        <Select value={formData.type} onChange={(value) => onFieldChange('type', value)}>
          <Option value="MOT">MOT</Option>
          <Option value="Service">Service</Option>
          <Option value="Other">Other</Option>
        </Select>
      </Form.Item>

      <Form.Item label="Engine Type">
        <Input value={formData.engineType} onChange={(event) => onFieldChange('engineType', event.target.value)} />
      </Form.Item>

      <Form.Item label="Category">
        <Input value={formData.category} onChange={(event) => onFieldChange('category', event.target.value)} />
      </Form.Item>

      {showAccountingFields ? (
        <>
          <Form.Item label="Account Code">
            <InputNumber
              style={{ width: '100%' }}
              value={formData.accountCode == null ? undefined : formData.accountCode}
              onChange={(value) => onFieldChange('accountCode', value == null ? null : value)}
            />
          </Form.Item>

          <Form.Item label="Account Code Purchases">
            <InputNumber
              style={{ width: '100%' }}
              value={formData.accountCodePurchases == null ? undefined : formData.accountCodePurchases}
              onChange={(value) => onFieldChange('accountCodePurchases', value == null ? null : value)}
            />
          </Form.Item>

          <Form.Item label="Item Code">
            <Input value={formData.itemCode} onChange={(event) => onFieldChange('itemCode', event.target.value)} />
          </Form.Item>

          <Form.Item>
            <Checkbox checked={formData.vatExempt} onChange={(event) => onFieldChange('vatExempt', event.target.checked)}>
              Vat Exempt
            </Checkbox>
          </Form.Item>
        </>
      ) : null}

      {showStatus ? (
        <Form.Item label="Status">
          <Select value={formData.status} onChange={(value) => onFieldChange('status', value)}>
            <Option value="active">active</Option>
            <Option value="deleted">deleted</Option>
          </Select>
        </Form.Item>
      ) : null}
    </>
  );
}

export default PriceFormFields;
